import React, { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";

import useScrollPositionThrottled from "../../hooks/useScrollPositionThrottled";
import { getSuggestedPosts } from "../../services/postService";

import MobileHeader from "../Header/MobileHeader/MobileHeader";
import SearchBox from "../SearchBox/SearchBox";
import TextButton from "../Button/TextButton/TextButton";
import UserCard from "../UserCard/UserCard";
import PreviewImage from "../PreviewImage/PreviewImage";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";
import ImageGrid from "../ImageGrid/ImageGrid";
import { normalizeMediaType } from "../../utils/media";

const SuggestedPosts = ({ token, showModal, showAlert }) => {
  const history = useHistory();
  const [result, setResult] = useState([]);
  const [search, setSearch] = useState(false);
  const [posts, setPosts] = useState({
    posts: null,
    fetching: false,
    hasMore: false,
  });

  const handlePostDialogDispatch = useCallback((action) => {
    if (!action || action.type !== "SET_POST_VOTES_COUNT") {
      return;
    }
    const {
      postId,
      postVotes,
      voteCount,
      reactionType,
      likeCount: nextLikeCount,
    } = action.payload || {};
    if (!postId) {
      return;
    }
    setPosts((previous) => {
      if (!previous.posts) {
        return previous;
      }
      const updatedPosts = previous.posts.map((post) => {
        const currentId =
          (post._id && post._id.toString()) ||
          (post.postId && post.postId.toString());
        if (currentId !== postId) {
          return post;
        }
        const hadReaction = post.reactionType === "like";
        const hasReaction = reactionType === "like";
        const incomingLikeCount =
          typeof nextLikeCount === "number" ? nextLikeCount : null;
        let likeCount =
          incomingLikeCount !== null ? incomingLikeCount : post.likeCount || 0;
        if (incomingLikeCount === null && typeof reactionType !== "undefined") {
          if (hasReaction && !hadReaction) {
            likeCount += 1;
          } else if (!hasReaction && hadReaction) {
            likeCount = Math.max(0, likeCount - 1);
          }
        }
        return {
          ...post,
          likeCount,
          postVotes: Array.isArray(postVotes) ? postVotes : post.postVotes,
          postVotesCount:
            typeof voteCount === "number" ? voteCount : post.postVotesCount,
          reactionType:
            typeof reactionType !== "undefined"
              ? reactionType
              : post.reactionType,
        };
      });
      return { ...previous, posts: updatedPosts };
    });
  }, []);

  const handleClick = (postId, avatar) => {
    if (window.outerWidth <= 600) {
      history.push(`/post/${postId}`);
    } else {
      showModal(
        {
          postId: postId.toString(),
          avatar,
          profileDispatch: handlePostDialogDispatch,
        },
        "PostDialog/PostDialog"
      );
    }
  };

  const retrievePosts = async (offset) => {
    try {
      setPosts((previous) => ({ ...previous, fetching: true }));
      const response = await getSuggestedPosts(token, offset);
      setPosts((previous) => ({
        posts: previous.posts ? [...previous.posts, ...response] : response,
        fetching: false,
        hasMore: response.length === 20,
      }));
    } catch (err) {
      showAlert(err.message);
    }
  };

  const retrievePostsRef = useRef(retrievePosts);

  const parseMetadata = (metadata) => {
    if (!metadata) {
      return null;
    }
    if (typeof metadata === "object") {
      return metadata;
    }
    try {
      return JSON.parse(metadata);
    } catch (err) {
      return null;
    }
  };

  const getMediaProps = (post) => {
    const mediaItem = post.mediaItems && post.mediaItems[0];
    if (!mediaItem) {
      return {
        mediaUrl: null,
        thumbnail: null,
        mediaType: "image",
        metadata: null,
        filter: "",
      };
    }

    return {
      mediaUrl: mediaItem.mediaUrl,
      thumbnail: mediaItem.thumbnailUrl || mediaItem.mediaUrl,
      mediaType: normalizeMediaType(mediaItem.mediaType, mediaItem.mediaUrl),
      metadata: parseMetadata(mediaItem.metadata),
      filter: mediaItem.filter || "",
    };
  };

  useEffect(() => {
    retrievePostsRef.current();
  }, [retrievePostsRef]);

  useScrollPositionThrottled(
    ({ atBottom }) => {
      if (atBottom && posts.hasMore && !posts.fetching) {
        retrievePosts(posts.posts.length);
      }
    },
    null,
    [posts]
  );

  const renderSkeleton = (amount) => {
    const skeleton = [];
    for (let i = 0; i < amount; i++) {
      skeleton.push(
        <SkeletonLoader key={i} style={{ minHeight: "30rem" }} animated />
      );
    }
    return skeleton;
  };

  return (
    <Fragment>
      <MobileHeader
        style={
          search && {
            gridTemplateColumns: "repeat(2, 1fr) min-content",
            gridColumnGap: "2rem",
          }
        }
      >
        <SearchBox
          style={{ gridColumn: `${search ? "1 / span 2" : "1 / -1"}` }}
          setResult={setResult}
          onClick={() => setSearch(true)}
        />
        {search && (
          <TextButton onClick={() => setSearch(false)} bold large>
            Cancel
          </TextButton>
        )}
      </MobileHeader>
      {search ? (
        <div className="explore-users">
          {result.map((user) => (
            <UserCard
              avatar={user.avatar}
              username={user.username}
              subText={user.fullName}
            />
          ))}
        </div>
      ) : (
        <ImageGrid>
          {posts.posts &&
            posts.posts.map((post, idx) => {
              const media = getMediaProps(post);
              const aggregatedReactions =
                (post.likeCount || 0) +
                (post.loveCount || 0) +
                (post.hahaCount || 0);
              const likes =
                aggregatedReactions > 0
                  ? aggregatedReactions
                  : typeof post.postVotesCount === "number"
                  ? post.postVotesCount
                  : Array.isArray(post.postVotes)
                  ? post.postVotes.length
                  : Number(post.postVotes) || 0;
              return (
                <PreviewImage
                  key={idx}
                  image={post.mediaItems?.[0]?.mediaUrl}
                  thumbnail={media.thumbnail}
                  mediaUrl={media.mediaUrl}
                  mediaType={media.mediaType}
                  metadata={media.metadata}
                  likes={likes}
                  comments={post.comments}
                  filter={media.filter}
                  onClick={() => handleClick(post.postId, post.author.avatar)}
                />
              );
            })}
          {posts.fetching && renderSkeleton(10)}
        </ImageGrid>
      )}
    </Fragment>
  );
};

export default SuggestedPosts;
